import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  cancelBooking,
  createBooking,
  formatMoney,
  listPayments,
  payBooking,
  paymentStatus,
} from '../../api/bookingApi';
import { useAuth } from '../../auth/AuthContext';
import { useBooking } from '../../booking/BookingContext';
import {
  detectTzMobileNetwork,
  formatTzPhoneDisplay,
  isValidTzPhone,
  normalizeTzPhone,
  tzNetworkLabel,
} from '../../booking/phone';
import { extractPaymentControlNumber } from '../../booking/paymentRef';
import IntlPhoneField from '../../components/IntlPhoneField';

const NETWORKS = [
  { id: 'mpesa', label: 'M-Pesa', logo: '/images/payment/mpesa_logo.png' },
  { id: 'tigopesa', label: 'Tigo', logo: '/images/payment/tigo_logo.png' },
  { id: 'halopesa', label: 'HaloPesa', logo: '/images/payment/halopesa_logo.png' },
  { id: 'airtel', label: 'Airtel', logo: '/images/payment/airtel_logo.png' },
];

function cleanInstructions(raw) {
  return String(raw || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .trim();
}

function fallbackSteps(id) {
  const business = '009009';
  switch (id) {
    case 'tigopesa':
      return `TIGO PESA\n1. Dial *150*01#\n2. Select Pay bills\n3. Enter business number ${business}\n4. Enter your booking reference as the account\n5. Enter the amount\n6. Confirm with your PIN`;
    case 'halopesa':
      return `HALOPESA\n1. Dial *150*88#\n2. Follow the prompts to pay\n3. Use business number ${business}\n4. Enter your booking reference\n5. Enter the amount and confirm`;
    case 'airtel':
      return `AIRTEL MONEY\n1. Dial *150*60#\n2. Select Make payments\n3. Enter business number ${business}\n4. Enter your booking reference as the account\n5. Enter the amount\n6. Confirm with your PIN`;
    case 'mpesa':
    default:
      return `VODACOM M-PESA\n1. Dial *150*00#\n2. Select Pay by M-Pesa\n3. Select Paybill\n4. Enter business number ${business}\n5. Enter your booking reference as the account\n6. Enter the amount\n7. Confirm with your PIN`;
  }
}

function dedupeMethods(methods) {
  const byId = {};
  for (const m of methods || []) {
    const id = String(m.id || '');
    if (!id) continue;
    const prev = byId[id];
    if (!prev) {
      byId[id] = { ...m };
      continue;
    }
    const merged = { ...prev };
    if (m.canInitiate && !prev.canInitiate) {
      merged.canInitiate = true;
      merged.token = m.token;
      merged.mode = m.mode || 'push';
      merged.phoneNumber = m.phoneNumber || merged.phoneNumber;
      merged.name = m.name || merged.name;
    }
    if (String(m.instructions || '').length > String(merged.instructions || '').length) {
      merged.instructions = m.instructions;
    }
    byId[id] = merged;
  }
  const order = ['mpesa', 'tigopesa', 'halopesa', 'airtel'];
  return [
    ...order.filter((id) => byId[id]).map((id) => byId[id]),
    ...Object.entries(byId)
      .filter(([id]) => !order.includes(id))
      .map(([, v]) => v),
  ];
}

export default function PaymentPage() {
  const booking = useBooking();
  const auth = useAuth();
  const navigate = useNavigate();
  const trip = booking.selectedTrip;

  const [payPhone, setPayPhone] = useState(() => booking.contactPhone || '');
  const [method, setMethod] = useState(
    () => detectTzMobileNetwork(booking.contactPhone) || 'mpesa'
  );
  const [live, setLive] = useState(booking.booking);
  const [methods, setMethods] = useState(
    () => dedupeMethods(booking.booking?.paymentMethods || [])
  );
  const [pushing, setPushing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [pushSent, setPushSent] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpLoading, setHelpLoading] = useState(false);
  const [error, setError] = useState('');

  const detected = detectTzMobileNetwork(payPhone);

  // Selected SafariYetu method is authoritative; prefix is a soft default only.
  const chosen = useMemo(() => {
    const selected = methods.find((m) => String(m.id) === method);
    if (selected) return selected;
    if (detected) {
      const match = methods.find((m) => String(m.id) === detected);
      if (match) return match;
    }
    return methods.find((m) => m.canInitiate) || methods[0] || null;
  }, [methods, detected, method]);

  const pushMethod = useMemo(() => {
    const selected = methods.find(
      (m) => String(m.id) === method && m.canInitiate
    );
    if (selected) return selected;
    if (detected) {
      const match = methods.find(
        (m) => String(m.id) === detected && m.canInitiate
      );
      if (match) return match;
    }
    return methods.find((m) => m.canInitiate) || null;
  }, [methods, detected, method]);

  const canPush = Boolean(pushMethod);
  const reference = String(live?.reference || '').trim();

  const bookingContactPhone = useMemo(() => {
    return (
      normalizeTzPhone(live?.contactPhone) ||
      normalizeTzPhone(live?.passengers?.[0]?.phone) ||
      null
    );
  }, [live]);

  const helpInstructions = useMemo(() => {
    let base = cleanInstructions(chosen?.instructions);
    if (base.length <= 20) {
      for (const m of methods) {
        const text = cleanInstructions(m.instructions);
        if (text.length > 20) {
          base = text;
          break;
        }
      }
    }
    if (base.length <= 20) base = fallbackSteps(detected || method);
    if (!reference) return base;
    if (base.toLowerCase().includes(reference.toLowerCase())) return base;
    return `${base}\n\nAccount / reference: ${reference}`;
  }, [chosen, methods, detected, method, reference]);

  const paymentAccount = useMemo(
    () => extractPaymentControlNumber(helpInstructions, reference),
    [helpInstructions, reference]
  );

  if (!trip) return <Navigate to="/book/trips" replace />;
  if (!booking.passengerDetails?.length) {
    return <Navigate to="/book/details" replace />;
  }

  const applyMethods = (list) => {
    const next = dedupeMethods(list);
    setMethods(next);
    const stillValid = next.find((m) => String(m.id) === method);
    if (stillValid) return;
    const det = detectTzMobileNetwork(payPhone);
    const hinted =
      (det && next.find((m) => String(m.id) === det)) ||
      next.find((m) => m.canInitiate) ||
      next[0];
    if (hinted?.id) setMethod(String(hinted.id));
  };

  const ensureBooking = async (phoneE164) => {
    const phoneMatches =
      bookingContactPhone != null && bookingContactPhone === phoneE164;

    if (live?.tripId && !phoneMatches) {
      try {
        await cancelBooking(live.tripId);
      } catch {
        /* ignore */
      }
      setLive(null);
      setMethods([]);
      setPushSent(false);
    } else if (live?.tripId && phoneMatches) {
      return live;
    }

    booking.setContact({ phone: phoneE164 });

    const passengers = booking.passengerDetails.map((p) => ({
      ...p,
      phone: phoneE164,
    }));

    const created = await createBooking({
      trip,
      seats: booking.selectedSeats,
      passengers,
      contact: {
        phone: phoneE164,
        ...(booking.contactEmail ? { email: booking.contactEmail } : {}),
        ...(booking.emergencyName
          ? { emergencyName: booking.emergencyName }
          : {}),
        ...(booking.emergencyPhone
          ? { emergencyPhone: booking.emergencyPhone }
          : {}),
        ...(booking.emergencyEmail
          ? { emergencyEmail: booking.emergencyEmail }
          : {}),
      },
      pickupPoint: booking.pickupPoint,
      dropoffPoint: booking.dropoffPoint,
    });

    let paymentMethods = created.paymentMethods || [];
    if (created.tripId) {
      try {
        const fresh = await listPayments(created.tripId);
        if (fresh.length) paymentMethods = fresh;
      } catch {
        /* keep create response methods */
      }
    }
    setLive(created);
    booking.setBooking(created);
    applyMethods(paymentMethods);
    return created;
  };

  const goTicket = async (updated) => {
    const paid = {
      ...updated,
      status: 'paid',
      trip,
      seats: booking.selectedSeats,
      passengers: booking.passengerDetails,
      amount: updated.amount > 0 ? updated.amount : booking.total,
      currency: trip.currency || 'TZS',
    };
    booking.setBooking(paid);
    try {
      await auth.rememberTicket(paid);
    } catch {
      /* local still tries inside rememberTicket */
    }
    navigate('/book/ticket', { replace: true });
  };

  const sendPush = async () => {
    if (pushing) return;
    const phoneE164 = normalizeTzPhone(payPhone);
    if (!phoneE164) {
      setError('Enter the mobile money number that will pay.');
      return;
    }
    setPushing(true);
    setError('');
    try {
      const current = await ensureBooking(phoneE164);
      let methodList = dedupeMethods(current.paymentMethods || []);
      try {
        const fresh = await listPayments(current.tripId);
        if (fresh.length) {
          methodList = dedupeMethods(fresh);
          applyMethods(fresh);
        }
      } catch {
        applyMethods(methodList);
      }

      const selected =
        methodList.find((m) => String(m.id) === method && m.canInitiate) ||
        null;
      const net = detectTzMobileNetwork(payPhone);
      const push =
        selected ||
        (net && methodList.find((m) => m.id === net && m.canInitiate)) ||
        methodList.find((m) => m.canInitiate) ||
        null;

      if (!push) {
        const label =
          tzNetworkLabel(method) || tzNetworkLabel(net) || 'this number';
        setError(
          `Push is not available for ${label}. Use the dial steps below, or try a different mobile money number.`
        );
        setShowHelp(true);
        return;
      }

      const methodId = String(push.id || method || net);
      setMethod(methodId);
      const updated = await payBooking({
        tripId: current.tripId,
        method: methodId,
        phoneOverride: phoneE164,
        token: push.token,
      });
      setLive(updated);
      booking.setBooking(updated);
      if (updated.paymentMethods?.length) {
        applyMethods(updated.paymentMethods);
      }
      setPushSent(true);
      if (String(updated.status).toLowerCase() === 'paid') {
        goTicket(updated);
      }
    } catch (err) {
      setError(err.message || 'Could not start payment.');
      setShowHelp(true);
    } finally {
      setPushing(false);
    }
  };

  const checkPaid = async () => {
    if (!live?.tripId) return;
    setChecking(true);
    setError('');
    try {
      const data = await paymentStatus(live.tripId);
      const statusMap = data.status;
      const bookingMap = data.booking;
      const paid =
        (statusMap && statusMap.paymentCompleted === true) ||
        bookingMap?.paymentCompleted === true;
      if (paid) {
        goTicket({
          ...live,
          reference: bookingMap?.reference || live.reference,
          tripId: bookingMap?.tripId || live.tripId,
          amount: bookingMap?.amount || live.amount || booking.total,
          qr: bookingMap?.ticket?.qr || live.qr,
          gate: bookingMap?.ticket?.gate || live.gate,
          status: 'paid',
        });
        return;
      }
      setError('Not received yet. Approve on your phone, then try again.');
    } catch (err) {
      setError(err.message || 'Could not check payment.');
    } finally {
      setChecking(false);
    }
  };

  const openHelp = async () => {
    setShowHelp(true);
    setError('');
    if (reference) return;
    const phoneE164 = normalizeTzPhone(payPhone);
    if (!phoneE164) {
      setError('Enter the mobile money number that will pay.');
      return;
    }
    setHelpLoading(true);
    try {
      await ensureBooking(phoneE164);
    } catch (err) {
      setError(err.message || 'Could not get booking reference.');
    } finally {
      setHelpLoading(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setError('');
      // brief non-blocking feedback via error slot would be wrong; use alert-less status
      const prev = document.title;
      document.title = label;
      setTimeout(() => {
        document.title = prev;
      }, 800);
    } catch {
      setError('Could not copy. Please copy manually.');
    }
  };

  const onCancel = async () => {
    if (live?.tripId) {
      try {
        await cancelBooking(live.tripId);
      } catch {
        /* still leave */
      }
    }
    booking.reset();
    navigate('/');
  };

  const busy = pushing || helpLoading;
  const networkLabel =
    tzNetworkLabel(method) ||
    String(chosen?.name || '')
      .split(' - ')[0]
      .trim() ||
    tzNetworkLabel(detected) ||
    'M-Pesa';

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-pink-200 bg-white p-5 shadow-sm text-center">
        <p className="text-sm text-gray-500">Total due</p>
        <p className="font-heading font-bold text-3xl text-superfeo-pink mt-1">
          {formatMoney(booking.total, trip.currency || 'TZS')}
        </p>
      </div>

      <div className="rounded-2xl border border-pink-100 bg-white p-4 shadow-sm space-y-4">
        <div className="flex flex-wrap justify-center gap-3">
          {NETWORKS.map((n) => {
            const available = methods.find((m) => String(m.id) === n.id);
            const active = method === n.id;
            const canPick =
              !methods.length || available?.canInitiate || Boolean(available);
            return (
              <button
                key={n.id}
                type="button"
                disabled={!canPick && methods.length > 0}
                onClick={() => {
                  setMethod(n.id);
                  if (pushSent) setPushSent(false);
                }}
                aria-label={n.label}
                className={`flex flex-col items-center justify-center gap-1.5 min-w-[72px] px-3 py-2.5 rounded-xl border transition ${
                  active
                    ? 'bg-pink-50 border-superfeo-pink shadow-sm'
                    : canPick || !methods.length
                      ? 'bg-white border-gray-200 hover:border-pink-200'
                      : 'bg-gray-50 border-gray-100 opacity-40 cursor-not-allowed'
                }`}
              >
                <img
                  src={n.logo}
                  alt=""
                  className="h-7 w-auto max-w-[64px] object-contain"
                />
                <span
                  className={`text-[10px] font-bold ${
                    active ? 'text-superfeo-pink' : 'text-gray-600'
                  }`}
                >
                  {n.label}
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-center text-gray-400">
          Choose the wallet that will receive the payment prompt
        </p>

        <label className="block text-sm font-medium text-gray-700">
          Mobile money number
          <div className="mt-1.5">
            <IntlPhoneField
              value={payPhone}
              tanzaniaOnly
              onChangeE164={(number) => {
                setPayPhone(number);
                const net = detectTzMobileNetwork(number);
                // Soft hint only — customer can still pick another method above.
                if (net) setMethod(net);
                if (pushSent) setPushSent(false);
              }}
              autoComplete="tel"
            />
          </div>
        </label>
        {isValidTzPhone(payPhone) && (
          <p className="text-xs text-gray-500">
            Paying with {networkLabel} · {formatTzPhoneDisplay(payPhone)}
            {detected && detected !== method
              ? ` (number looks like ${tzNetworkLabel(detected)})`
              : ''}
          </p>
        )}

        {pushSent && (
          <div className="bg-green-50 border border-green-100 rounded-xl px-3 py-2 text-sm font-semibold text-green-800 flex items-center gap-2">
            <span>✓</span> Waiting for you to approve…
          </div>
        )}

        <button
          type="button"
          disabled={busy || checking}
          onClick={pushSent ? checkPaid : sendPush}
          className="w-full h-14 bg-superfeo-pink hover:bg-superfeo-darkpink disabled:bg-gray-300 text-white font-extrabold rounded-xl"
        >
          {pushing || checking
            ? pushing
              ? 'Sending…'
              : 'Checking…'
            : pushSent
              ? 'I’ve paid'
              : 'Pay now'}
        </button>

        {pushSent && !pushing && (
          <button
            type="button"
            disabled={busy || checking}
            onClick={sendPush}
            className="w-full text-sm font-semibold text-superfeo-pink"
          >
            Send again
          </button>
        )}

        {error && (
          <p className="text-center text-sm text-red-600">{error}</p>
        )}

        {showHelp ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-left space-y-3">
            <p className="font-heading font-bold text-gray-900">
              Pay by dialing USSD
            </p>
            {helpLoading ? (
              <p className="text-sm text-gray-500">
                Getting your booking reference…
              </p>
            ) : paymentAccount || reference ? (
              <div className="flex items-center gap-2 rounded-xl border border-pink-200 bg-pink-50 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-superfeo-darkpink">
                    Account / reference
                  </p>
                  <p className="font-heading font-bold text-lg break-all">
                    {paymentAccount || reference}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    copyText(paymentAccount || reference, 'Reference copied')
                  }
                  className="text-superfeo-pink text-sm font-bold shrink-0"
                >
                  Copy
                </button>
              </div>
            ) : (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                Tap <strong>Pay now</strong> first so we can create your booking
                and show the reference number for USSD.
              </p>
            )}
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">
              {helpInstructions}
            </pre>
            <div className="flex justify-between gap-2">
              <button
                type="button"
                onClick={() => copyText(helpInstructions, 'Steps copied')}
                className="text-sm font-semibold text-superfeo-pink"
              >
                Copy steps
              </button>
              <button
                type="button"
                onClick={() => setShowHelp(false)}
                className="text-sm font-semibold text-gray-500"
              >
                Hide
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={openHelp}
            className="w-full h-11 rounded-xl border border-gray-200 font-bold text-sm text-gray-800"
          >
            Need help paying?
          </button>
        )}

        {!canPush && methods.length > 0 && (
          <p className="text-center text-xs text-gray-500">
            Push may be unavailable — use USSD help with your reference.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        className="w-full text-sm font-semibold text-gray-500 py-2"
      >
        Cancel
      </button>
    </div>
  );
}
